import { useEffect, useMemo, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import type { MemberRecord } from '@/store/types';
import styles from '@/components/panels.module.css';

export interface MentionPickerProps {
  query: string;
  /**
   * The members this account can currently see.
   *
   * Section 06: "Mentions must resolve to visible members." The list is the
   * conversation's own membership projection, so a name that is not in it
   * cannot be chosen — a mention of someone the account cannot see would be
   * rejected by the server and would leak the fact that they exist.
   */
  members: readonly MemberRecord[];
  onChoose: (member: MemberRecord) => void;
  onDismiss: () => void;
}

const MAX_SUGGESTIONS = 8;

/** The member list filtered by what has been typed after the `@`. */
export function MentionPicker(props: MentionPickerProps) {
  const { t } = useStrings();
  const [highlighted, setHighlighted] = useState(0);

  const matches = useMemo(() => {
    const needle = props.query.trim().toLocaleLowerCase();
    return props.members
      .filter((member) => !member.banned)
      .filter((member) => {
        if (needle.length === 0) {
          return true;
        }
        return (
          member.displayName.toLocaleLowerCase().includes(needle) ||
          (member.username ?? '').toLocaleLowerCase().includes(needle)
        );
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [props.members, props.query]);

  useEffect(() => {
    setHighlighted(0);
  }, [props.query]);

  // Arrow keys and Enter are handled at the document level rather than on the
  // list, because focus stays in the textarea: moving it into the menu would
  // interrupt typing, which is the opposite of what an inline completion is
  // for.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (matches.length === 0) {
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlighted((index) => (index + 1) % matches.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlighted((index) => (index - 1 + matches.length) % matches.length);
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        const chosen = matches[highlighted];
        if (chosen) {
          event.preventDefault();
          props.onChoose(chosen);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        props.onDismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [matches, highlighted, props]);

  if (matches.length === 0) {
    return (
      <div className={styles.mentionMenu} role="listbox" aria-label={t('mention.label')}>
        <p className={styles.empty}>{t('mention.empty')}</p>
      </div>
    );
  }

  return (
    <div className={styles.mentionMenu} role="listbox" aria-label={t('mention.label')}>
      {matches.map((member, index) => (
        <button
          key={member.userId}
          type="button"
          role="option"
          aria-selected={index === highlighted}
          className={styles.mentionItem}
          onMouseEnter={() => {
            setHighlighted(index);
          }}
          onClick={() => {
            props.onChoose(member);
          }}
        >
          <span dir="auto">{member.displayName || member.userId.slice(0, 8)}</span>
          {member.username === null ? null : (
            <span className={styles.rowMeta}>@{member.username}</span>
          )}
        </button>
      ))}
    </div>
  );
}
